'use client'

import { useEffect, useState } from 'react'
import { slashCommandRegistry } from './registry'

type CommandRow = {
  id: string
  name: string
  description: string | null
  prompt: string | null
  chat_invocation_hint: string | null
}

function toSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

export function useCustomCommands(workspaceId?: string | null): number {
  const [version, setVersion] = useState(0)

  useEffect(() => {
    if (!workspaceId) return

    const controller = new AbortController()
    const registered: string[] = []

    async function load() {
      try {
        const response = await fetch('/api/commands?chat_invocable=true&enabled=true', {
          signal: controller.signal,
        })
        if (!response.ok) return
        const json = await response.json() as { commands?: CommandRow[] }
        for (const cmd of json.commands || []) {
          const name = toSlug(cmd.name || cmd.id)
          const prompt = cmd.chat_invocation_hint || cmd.prompt || cmd.description || `Run command ${cmd.name}`
          if (!name || !prompt) continue
          const existing = slashCommandRegistry.get(name)
          if (existing && !existing.custom) continue
          slashCommandRegistry.register({
            name,
            description: cmd.description || cmd.chat_invocation_hint || 'Custom command',
            tier: 'B',
            args: [],
            custom: true,
            promptTemplate: () => prompt,
          })
          registered.push(name)
        }
        setVersion(value => value + 1)
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('[slash-commands] custom command load failed', err)
        }
      }
    }

    void load()

    return () => {
      controller.abort()
      for (const name of registered) {
        slashCommandRegistry.unregister(name)
      }
      setVersion(value => value + 1)
    }
  }, [workspaceId])

  return version
}
